import type * as Api from '@ViewModels';
import { AppState } from '../../models/AppState';
import type { IRealtimeTranscriptionUtteranceEvent } from '../../viewModels/phonecall';
import type { IWebsocketSubscription } from '../../viewModels/websockets';
import type { TranscriptSource, TranscriptSpeaker, TranscriptUtterance } from './types';

const SUBSCRIPTION_NAME = 'focused-view:twilio-transcript';

/**
 * Map Twilio's track-name "speaker" field onto our neutral speaker bucket.
 *
 * Twilio's `inbound_track` / `outbound_track` labels are relative to *who
 * placed the call*, not to who is the lead. For an OUTBOUND call (rep dials
 * lead), `inbound_track` is the lead and `outbound_track` is the rep. For an
 * INBOUND call (lead dials in, rep answers), the labels flip — the rep is
 * the originating party from Twilio's perspective so `inbound_track` is the
 * rep and `outbound_track` is the lead. We pass the call direction in
 * through `TranscriptSourceContext` and flip accordingly.
 */
function mapSpeaker(track: string | undefined, direction: 'inbound' | 'outbound' | undefined): TranscriptSpeaker {
	if (track !== 'inbound_track' && track !== 'outbound_track') return 'other';
	const isInbound = direction === 'inbound';
	if (track === 'inbound_track') return isInbound ? 'rep' : 'lead';
	return isInbound ? 'lead' : 'rep';
}

function speakerLabelFor(speaker: TranscriptSpeaker): string | undefined {
	if (speaker === 'lead') return 'Lead';
	if (speaker === 'rep') return 'Sales Rep';
	return undefined;
}

/**
 * Twilio implementation of the `TranscriptSource` contract.
 *
 * Subscribes to the global `AppState.websockets` listener for
 * `RealtimeTranscriptionUtteranceEvent` messages, filters to the current
 * `phoneCallId`, and emits one `TranscriptUtterance` per event.
 *
 * The stability/partial-vs-final dedup logic lives in `useTranscriptStream`
 * — this source just normalizes the wire format and forwards everything.
 */
export const twilioTranscriptSource: TranscriptSource = {
	id: 'twilio',
	start(emit, context) {
		const websockets = AppState.websockets;
		if (!websockets) {
			return () => undefined;
		}

		const handle = (events: Api.IRemoteEvent<IRealtimeTranscriptionUtteranceEvent>[]) => {
			for (const event of events) {
				const value = event.value;
				if (!value) continue;
				if (context.phoneCallId && value.phoneCallId !== context.phoneCallId) continue;

				const speaker = mapSpeaker(value.speaker, context.direction);
				const seqRaw = value.sequenceId;
				const seqParsed = typeof seqRaw === 'string' ? parseInt(seqRaw, 10) : Number(seqRaw);
				const stabilityRaw = value.stability;
				const confidence = typeof stabilityRaw === 'string' ? parseFloat(stabilityRaw) : Number(stabilityRaw);

				const utterance: TranscriptUtterance = {
					sourceId: 'twilio',
					partialKey: value.speaker || 'unknown_track',
					providerSequenceId: Number.isFinite(seqParsed) ? seqParsed : undefined,
					speaker,
					speakerLabel: speakerLabelFor(speaker),
					text: value.transcription ?? '',
					isFinal: !!value.final,
					confidence: Number.isFinite(confidence) ? confidence : undefined,
					timestamp: new Date(),
				};

				emit(utterance);
			}
		};

		const subscription: IWebsocketSubscription<IRealtimeTranscriptionUtteranceEvent> = {
			callback: handle,
			events: ['RealtimeTranscriptionUtteranceEvent'],
			name: SUBSCRIPTION_NAME,
		};

		websockets.subscribe(subscription);
		return () => {
			websockets.unsubscribe(SUBSCRIPTION_NAME);
		};
	},
};
